import { useState, useEffect, useCallback } from "react"
import Sidebar from "./Sidebar"
import KPISection from "./KPISection"
import SummaryChart from "./SummaryChart"
import SubmissionCards from "./SubmissionCards"
import api from "../services/api"
import toast from "react-hot-toast"

export default function Dashboard() {
  const [esmpData, setEsmpData] = useState([])
  const [chartData, setChartData] = useState([])
  const [totalSubmissions, setTotalSubmissions] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showAllDistricts, setShowAllDistricts] = useState(false)

  // ✅ Move function ABOVE useEffect
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true)

      const esmpResponse = await api.get("/esmp/list")
      const esmps = esmpResponse.data.data || []

      setEsmpData(esmps)
      setTotalSubmissions(esmps.length)

      const districts = [
        "Balaka", "Blantyre", "Chikwawa", "Chiradzulu", "Chitipa",
        "Dedza", "Dowa", "Karonga", "Kasungu", "Likoma",
        "Lilongwe", "Machinga", "Mangochi", "Mchinji", "Mulanje",
        "Mwanza", "Mzimba", "Neno", "Nkhata Bay", "Nkhotakota",
        "Nsanje", "Ntcheu", "Ntchisi", "Phalombe", "Rumphi",
        "Salima", "Thyolo", "Zomba"
      ]

      let districtData = districts.map(district => {
        const districtEsmp = esmps.filter(e => e.district === district)
        const approved = districtEsmp.filter(e => e.status === "Approved").length

        return {
          district,
          submissions: districtEsmp.length,
          approved
        }
      })

      if (!showAllDistricts) {
        districtData = districtData.filter(d => d.submissions > 0)
      }

      districtData.sort((a, b) => b.submissions - a.submissions)
      setChartData(districtData)
    } catch (error) {
      console.error("Error fetching dashboard data:", error)
      toast.error("Failed to load dashboard data")
    } finally {
      setLoading(false)
    }
  }, [showAllDistricts]) // ✅ dependency added

  // ✅ ESLint-compliant useEffect
  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      <main className="flex-1 p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">ESMP TRACKING SYSTEM</h2>

          <select className="border border-white bg-white text-green-700 px-4 py-2 rounded-lg font-semibold shadow">
            <option>Last 7 days</option>
            <option>Last 30 days</option>
          </select>
        </div>

        {/* KPI Section */}
        <div className="bg-white p-6 rounded-xl shadow">
          <KPISection totalSubmissions={totalSubmissions} />
        </div>

        {/* Chart Section */}
        <div className="bg-white p-6 rounded-xl shadow">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-green-700">
              District Submissions Overview
            </h3>

            <button
              onClick={() => setShowAllDistricts(prev => !prev)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                showAllDistricts
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {showAllDistricts ? "Hide Empty Districts" : "Show All Districts"}
            </button>
          </div>

          <p className="text-xs text-gray-500 mb-3">
            {showAllDistricts
              ? "Showing all 28 districts"
              : `Showing ${chartData.length} districts with submissions`}
          </p>

          <SummaryChart data={chartData} />
        </div>

        {/* Submissions */}
        <div className="bg-white p-6 rounded-xl shadow">
          <h3 className="text-lg font-semibold mb-3 text-green-700">
            Recent ESMP Submissions
          </h3>

          {esmpData.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No ESMP submissions yet
            </p>
          ) : (
            <SubmissionCards data={esmpData.slice(-5).reverse()} />
          )}
        </div>
      </main>
    </div>
  )
}
